<template>
  <BaseLightBox :boxNumber="2" :mode="mode">
    <template #title>
      <v-skeleton-loader
        v-if="loading"
        type="text"
        width="200"
        class="skeleton-loader"
      ></v-skeleton-loader>
      <span v-else>{{ docTypeName }}</span>
    </template>

    <!-- Skeleton loading state for product widgets -->
    <template v-if="loading">
      <v-skeleton-loader
        v-for="n in 2"
        :key="n"
        type="list-item-avatar-three-line"
        class="mb-4 skeleton-loader skeleton-product"
      ></v-skeleton-loader>
    </template>

    <!-- Actual product widgets -->
    <template v-else>
      <ProductWidget
        :isCopyBtnLoading="loadingCopyLinesToCart"
        :isCopyAllLinesToCartLoading="loadingCopyAllLinesToCart"
        @addLineToCart="addLineToCart"
        @removeLineFromCart="removeLineFromCart"
        @copyLineToCart="copyLineToCart"
        v-for="line in sortedOrderLines"
        :key="line.id"
        :line="line"
        :isDisabledForCopy="isProductDisabledForCopy(line)"
        :orderNumber="ordname"
        :doc="doc"
        :isForReturn="doc?.isForReturn"
        :isQuantityBtnDisable="isQuantityBtnDisable"
        :orderReference="reference"
        :editAble="editable"
        :quantButtonTitle="quantButtonTitle"
        @updateCounter="updateLineInfo"
        @updateRefundReason="updateLineInfo"
        :mode="mode"
      >
      </ProductWidget>
    </template>

    <slot name="bottom-action-btn"></slot>

    <template
      v-if="mode === 'orders' && orderLines?.length > 1"
      #top-left-action-btn
    >
      <template v-if="!loading">
        <BaseButton
          v-if="!editable"
          @click="
            () => {
              this.$emit('setEditMode', true);
            }
          "
          paddingTop="8px"
          paddingBottom="8px"
          paddingLeft="10px"
          paddingRight="10px"
        >
          <v-icon
            class="ml-1"
            color="white"
            size="16"
            icon="mdi-content-copy"
          />
          <span class="btn-text">{{ $t("SingleOrder.copyLinesBtn") }}</span>
        </BaseButton>
        <div
          class="d-flex ga-5"
          :class="[isMobile ? 'flex-column' : '']"
          v-else
        >
          <BaseButton
            @click="
              () => {
                setEditMode(false);
              }
            "
            backgroundColor="#DCDCDC"
            textColor="black"
            paddingTop="8px"
            paddingBottom="8px"
            paddingLeft="10px"
            paddingRight="10px"
          >
            <v-icon class="ml-1" color="black" size="20px" icon="mdi-close" />
            <span class="btn-text">{{ $t("SingleOrder.cancelCopy") }}</span>
          </BaseButton>
          <BaseButton
            @click="copyAllLinesToCart"
            :loading="loadingCopyAllLinesToCart"
            backgroundColor="#f8991c "
            textColor="white"
            paddingTop="8px"
            paddingBottom="8px"
            paddingLeft="10px"
            paddingRight="10px"
          >
            <v-icon
              class="ml-1"
              color="white"
              size="20px"
              icon="mdi-content-copy"
            />
            <span class="btn-text">{{ $t("SingleOrder.copyAll") }}</span>
          </BaseButton>
          <BaseButton
            :loading="loadingCopyLinesToCart"
            backgroundColor="#4FB989"
            paddingTop="8px"
            paddingBottom="8px"
            paddingLeft="10px"
            paddingRight="10px"
            @click="addOrderLinesToCart"
          >
            <v-icon size="20px" class="ml-1" color="white" icon="mdi-plus" />
            <span class="btn-text">{{
              $t("SingleOrder.addMarkedLinesToCart")
            }}</span>
          </BaseButton>
        </div>
      </template>
    </template>

    <div class="doc-summary-section">
      <template v-if="loading">
        <v-skeleton-loader
          type="text"
          width="200"
          class="mr-4 skeleton-loader"
        ></v-skeleton-loader>
      </template>
      <slot v-else name="doc-summary"></slot>
    </div>
  </BaseLightBox>
</template>

<script>
import { mapGetters } from "vuex";
import BaseButton from "../UI/BaseButton.vue";
import BaseLightBox from "../UI/BaseLightBox.vue";
import ProductWidget from "./ProductWidget.vue";

export default {
  components: {
    BaseLightBox,
    ProductWidget,
    BaseButton,
  },
  emits: ["updateLineInfo", "setEditMode"],
  data() {
    return {
      selectedValue: null,
      productsToCart: [],
      loading: true,
      loadingCopyLinesToCart: false,
      loadingCopyAllLinesToCart: false,
    };
  },
  props: {
    mode: {
      type: String,
    },
    docTypeName: {
      type: String,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    orderLines: {
      type: Array,
    },
    reference: {
      type: String,
      default: null,
    },
    ordname: {
      type: String,
      default: null,
    },
    quantButtonTitle: {
      type: String,
    },
    isQuantityBtnDisable: {
      type: Boolean,
    },
    doc: {
      type: Object,
      default: null,
    },
  },
  watch: {
    orderLines: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          // Add a small delay to simulate loading
          setTimeout(() => {
            this.loading = false;
          }, 200);
        }
      },
    },
  },
  computed: {
    ...mapGetters({
      getLineByPart: "Orders/getLineByPart",
      isMobile: "isMobile",
    }),
    sortedOrderLines() {
      if (!this.orderLines) return [];
      const copyOrderLines = [...this.orderLines];
      copyOrderLines.sort((a, b) => a.line - b.line);
      return copyOrderLines;
    },
  },
  methods: {
    isProductDisabledForCopy(line) {
      return line?.Part?.categoryId === -1;
    },
    async copyAllLinesToCart() {
      this.orderLines.map((line) => this.addLineToCart(line));
      try {
        this.loadingCopyAllLinesToCart = true;
        await this.$store.dispatch(
          "Orders/updateLinesCart",
          this.productsToCart
        );
        this.$store.dispatch("Orders/setCartModel", true);
        this.setEditMode(false);
      } catch (error) {
        console.log(error);
      } finally {
        this.loadingCopyAllLinesToCart = false;
        this.productsToCart = [];
      }
    },
    async updateProduct(obj) {
      var { product, quantity } = obj;
      var lineOrdi = this.getLineByPart(product.part);

      var line = {
        Part: { ...product },
        quant: quantity,
      };
      if (this.isProductDisabledForCopy(line)) return;
      if (lineOrdi) line.ordi = lineOrdi;
      try {
        this.loadingCopyLinesToCart = true;
        if (quantity) {
          await this.$store.dispatch("Orders/updateCart", line);
        } else {
          await this.$store.dispatch("Orders/removeProductFromCart", line);
        }
        this.$store.dispatch("Orders/setCartModel", true);
      } catch (error) {
        console.log(error);
      } finally {
        this.loadingCopyLinesToCart = false;
      }
    },
    copyLineToCart(line) {
      this.updateProduct({ product: line.Part, quantity: line.quant });
    },
    setEditMode(editMode) {
      this.$emit("setEditMode", editMode);
    },
    async addOrderLinesToCart() {
      if (this.productsToCart.length > 0) {
        try {
          this.loadingCopyLinesToCart = true;
          await this.$store.dispatch(
            "Orders/updateLinesCart",
            this.productsToCart
          );
          this.$store.dispatch("Orders/setCartModel", true);
          this.setEditMode(false);
        } catch (error) {
          console.log(error);
        } finally {
          this.loadingCopyLinesToCart = false;
        }
      }
      this.productsToCart = [];
    },
    addLineToCart(line) {
      if (this.isProductDisabledForCopy(line)) return; // מוצר לא פעיל
      var lineOrdi = this.getLineByPart(line.Part.part);
      var lineToPush = {
        Part: { ...line.Part },
        quant: line.quant,
      };
      if (lineOrdi) {
        lineToPush.ordi = lineOrdi;
      }
      if (lineToPush.quant) {
        this.productsToCart.push(lineToPush);
      }
    },
    removeLineFromCart(line) {
      this.productsToCart = this.productsToCart.filter(
        (item) => item.line !== line.line
      );
    },
    updateLineInfo(updatedLine) {
      this.$emit("updateLineInfo", updatedLine);
    },
    isObjectEmpty(obj) {
      if (
        obj &&
        Object.keys(obj).length === 0 &&
        Object.getPrototypeOf(obj) === Object.prototype
      ) {
        return false;
      } else return true;
    },
  },
};
</script>

<style scoped lang="scss">
.doc-summary-section {
  display: flex;
  width: 100%;
  justify-content: end;
  margin-top: 30px;
}

.btn-text {
  font-size: $smallTextSize;
  font-weight: 400;
}

/* Fix for deep selector syntax */
::v-deep .skeleton-loader {
  border-radius: 8px;
}

::v-deep .skeleton-product {
  padding: 12px;
  background-color: #f5f5f5;
}
</style>
